import {
  ApiCamper,
  ApiCamperRate,
  ApiCamperSpecialOfferGroup,
  ApiGeoUnitWithDayConstraints,
  ApiGetCamperDetailsRequestFromJSON,
  ApiGetCamperDetailsResponse,
  ApiGetCamperListRequestFromJSON,
  ApiGetCamperListResponse,
  ApiGetGeoUnitsRequestFromJSON,
  ApiItemSelection,
  ApiItemType,
  ApiPagination,
  ApiSortDirection,
  ApiSortOption
} from '@ibe/api';
import { ApiService, errorEvent, LoggerFactory, Logger } from '@ibe/services';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { scroller } from 'react-scroll';
import Keys from '../../../_Translations/generated/camper.en.t.json.keys';
import { CamperSearchParams } from '../../SubModules/CamperSearch/DefaultCamperSearchSchema';
import ServiceListStore from '../../SubModules/ServiceList/ServiceListStore';
import CamperParams from '../../SubModules/CamperDetails/CamperParams';
import { CamperSearchResults, Tenant } from './externals';

const logger: Logger = LoggerFactory.get('CamperStore.ts');

class CamperStore extends ServiceListStore {
  isLoading = false;

  loadCamperLock = false;

  listViewInitialized = false;

  readonly initialPagination: ApiPagination = {
    startingFrom: 0,
    showing: 30,
    currentPage: 1,
    ofTotal: 0
  };

  _camperListResponse: ApiGetCamperListResponse | null = null;

  _camperDetailsResponse: ApiGetCamperDetailsResponse | null = null;

  _camperCount = 0;

  _omittedFeaturesVehicleCompare: string[] | undefined = [
    'bedRemark',
    'vehicleType',
    'vehicleWeightOver35t',
    'engine',
    'averageFuelConsumption',
    'transmission',
    'vehicleRemark',
    'popUpRoof',
    'bluetooth'
  ];

  geoUnits: ApiGeoUnitWithDayConstraints[];

  error: string | null = null;

  errorCamperNotFound = false;

  warning: string | null = null;

  searchParams: Partial<CamperSearchParams> = {};

  scrollElementGlobalError = 'camperGlobalErrorScroller';

  tenant: Tenant | null = null;

  constructor(private api: ApiService) {
    super();

    makeObservable(this, {
      isLoading: observable,
      loadCamperLock: observable,
      searchParams: observable,
      error: observable,
      errorCamperNotFound: observable,
      warning: observable,
      geoUnits: observable,
      listViewInitialized: observable,

      _camperListResponse: observable,
      _camperDetailsResponse: observable,

      hasSearchParams: computed,
      list: computed,
      hasList: computed,
      detail: computed,
      hasDetail: computed,
      omittedFeaturesVehicleCompare: computed,
      hasFilters: computed,

      setIsLoading: action,
      loadCampers: action,
      loadCamperDetail: action,
      reloadCamperDetailsFromPreselection: action,
      reloadCamperDetailsFromReplacedRate: action,
      setDetail: action,
      resetDetail: action,
      setSearchParams: action,
      setError: action,
      setListResponse: action,
      setTenant: action,
      setGeoUnits: action,
      setListViewInitialized: action
    });
  }

  setIsLoading(b: boolean): void {
    this.isLoading = b;
  }

  setGeoUnits(units: ApiGeoUnitWithDayConstraints[]): void {
    this.geoUnits = units;
  }

  private getMerchantCode(currency: string): string {
    let pCode = (this.tenant as string)?.toLowerCase() || '';
    if (pCode === Tenant.CB.valueOf().toLowerCase()) {
      switch (currency) {
        case 'CHF': {
          pCode += '-ch';
          break;
        }
        default:
          pCode += '-de';
      }
    } else {
      switch (currency) {
        case 'AU$': {
          pCode += '-au';
          break;
        }
        case 'GBP': {
          pCode += '-uk';
          break;
        }
        default:
          pCode += '-eu';
      }
    }
    return pCode;
  }

  private retrieveGeoUnits = async (): Promise<ApiGeoUnitWithDayConstraints[]> => {
    return Object.entries(
      (
        await this.api.getGeoUnits(
          ApiGetGeoUnitsRequestFromJSON({
            type: 'GetGeoUnitsRequest',
            itemType: ApiItemType.CAMPER,
            typeCodes: ['COUNTRY']
          })
        )
      ).geoUnits
    ).map(([, geoUnit]) => geoUnit as ApiGeoUnitWithDayConstraints);
  };

  private async getCountryCode(pickUpStationCode: string): Promise<string | undefined> {
    if (!this.geoUnits || this.geoUnits.length === 0) {
      this.setGeoUnits(await this.retrieveGeoUnits());
    }

    const unit = this.geoUnits.find(
      u =>
        u.typeCode === 'COUNTRY' &&
        Object.values(u.children).find(c => c.code === pickUpStationCode)
    );
    return unit?.code.toLowerCase();
  }

  getSupplierIframeSrc = (currency: string): Promise<string> => {
    const pickUpCode =
      this.searchParams.pickupStationGeoUnit?.code || this.searchParams.pickupStation || '';

    return this.getCountryCode(pickUpCode).then(cc => {
      return (
        'https://www.camper-content.com/' +
        this.getMerchantCode(currency) +
        '/comparison/' +
        cc +
        '/' +
        this.searchParams.startDate
      );
    });
  };

  get omittedFeaturesVehicleCompare(): string[] | undefined {
    return this._omittedFeaturesVehicleCompare;
  }

  get list(): ApiCamper[] {
    return (this._camperListResponse || { campers: [] }).campers;
  }

  get hasList(): boolean {
    return this._camperListResponse != null && this._camperListResponse.listOptions?.count > 0;
  }

  get detail(): ApiCamper | undefined {
    return this._camperDetailsResponse?.camper || undefined;
  }

  termsAndConditions = async (
    camper?: ApiCamper,
    promotion?: string,
    brand?: string
  ): Promise<string | undefined> => {
    const camperItem = camper || this.detail;
    if (!!camperItem) {
      try {
        return (
          await this.api.getCamperTermsAndConditions({
            camper: camperItem,
            promotion: promotion,
            brand: brand
          })
        )?.url;
      } catch (err) {
        logger.error(err);
      }
    }
    return undefined;
  };

  get hasDetail(): boolean {
    return !!this.detail;
  }

  get hasFilters(): boolean {
    return Object.keys(this._filter).length > 0;
  }

  setListResponse(camperSearchResults: CamperSearchResults): void {
    if (this._camperListResponse) {
      this._camperListResponse.campers = camperSearchResults.campers;
    } else {
      this._camperListResponse = {
        campers: camperSearchResults.campers,
        listOptions: camperSearchResults.listOptions
      };
    }
    this.setListOptions(camperSearchResults.listOptions);
    this.setSearchParams(camperSearchResults.searchParams);
  }

  setDetail(camper: ApiCamper): void {
    if (this._camperDetailsResponse) {
      this._camperDetailsResponse.camper = camper;
    }
  }

  setSearchParams(p: Partial<CamperSearchParams>): void {
    if (this.isNewSearch(p)) {
      this.resetFilters();
    }
    this.searchParams = p;
  }

  setTenant(tenant: Tenant): void {
    this.tenant = tenant;
  }

  setListViewInitialized(init: boolean): void {
    this.listViewInitialized = init;
  }

  isNewSearch(p: Partial<CamperSearchParams>): boolean {
    return (
      p.dropOffStation !== this.searchParams.dropOffStation ||
      p.pickupStation !== this.searchParams.pickupStation
    );
  }

  resetDetail(): void {
    this._camperDetailsResponse = null;
    this.setListViewInitialized(false);
  }

  get hasSearchParams(): boolean {
    return Object.keys(this.searchParams || {}).length > 0;
  }

  resetErrorMessages(): void {
    this.error = null;
    this.warning = null;
    this.errorCamperNotFound = false;
  }

  async loadCampers(
    searchParams?: Partial<CamperParams>,
    affiliateNumber?: string
  ): Promise<ApiCamper[]> {
    this.resetErrorMessages();
    const req = ApiGetCamperListRequestFromJSON({
      type: 'GetCamperListRequest',
      pickupStation: searchParams?.pickupStation || this.searchParams.pickupStation,
      dropOffStation: searchParams?.dropOffStation || this.searchParams.dropOffStation,
      startDate: searchParams?.startDate || this.searchParams.startDate,
      endDate: searchParams?.endDate || this.searchParams.endDate,
      adults: searchParams?.adults || this.searchParams.adults,
      children: searchParams?.children || this.searchParams.children,
      listOptions: {
        filter: this.filter,
        pagination: this.pagination || {},
        sorting: this.sorting || {
          sortDirection: ApiSortDirection.ASC,
          sortOption: ApiSortOption.PRICE
        }
      },
      affiliate: affiliateNumber
    });

    let res: ApiGetCamperListResponse | null = null;
    let error: string | null = null;
    const warning: string | null = null;

    try {
      res = await this.api.getCamperList(req);
    } catch (e) {
      logger.warn(e);
      error = Keys.apiNotAbleToGetCampersResults;
    }
    runInAction(() => {
      this._camperListResponse = res;
      if (res) {
        this._camperCount = res.campers.length;
        if (res.listOptions) {
          this.setListOptions(res.listOptions);
        }
      }
      this.error = error;
      this.warning = warning;
      if (this._camperCount === 0) {
        this.warning = Keys.emptyResultsWarning;
        if (!!this.listCountFull && this.listCountFull > 0) {
          this.warning = Keys.emptyFilteringResultsWarning;
        }
      }
    });
    return new Promise(resolve => resolve(this._camperListResponse?.campers as ApiCamper[]));
  }

  async loadCamperDetail(
    camperId: string,
    searchParams?: Partial<CamperParams>,
    selectedSpecialOfferCodes?: Array<string>,
    selectedExtras?: Array<ApiItemSelection>,
    selectedRate?: string,
    affiliateNumber?: string
  ): Promise<ApiGetCamperDetailsResponse | null> {
    this.resetErrorMessages();
    const req = ApiGetCamperDetailsRequestFromJSON({
      type: 'GetCamperDetailsRequest',
      pickupStation: searchParams?.pickupStation || this.searchParams.pickupStation,
      dropOffStation: searchParams?.dropOffStation || this.searchParams.dropOffStation,
      startDate: searchParams?.startDate || this.searchParams.startDate,
      endDate: searchParams?.endDate || this.searchParams.endDate,
      adults: searchParams?.adults || this.searchParams.adults,
      children: searchParams?.children || this.searchParams.children,
      selectedSpecialOfferCodes: selectedSpecialOfferCodes,
      selectedExtras: selectedExtras,
      productId: camperId,
      affiliate: affiliateNumber
    });

    let res: ApiGetCamperDetailsResponse | null = null;
    let error: string | null = null;

    try {
      this.loadCamperLock = true;
      res = await this.api.getCamperDetails(req);
    } catch (e) {
      logger.warn(e);
      error = Keys.apiNotAbleToGetCampersDetails;
      this.loadCamperLock = false;
    }
    runInAction(() => {
      this._camperDetailsResponse = res;
      this.error = error;
      this.errorCamperNotFound = !!error;
      if (selectedRate !== undefined && this._camperDetailsResponse?.camper !== undefined) {
        this._camperDetailsResponse.camper = {
          ...this._camperDetailsResponse?.camper,
          rates: this._camperDetailsResponse?.camper.rates?.map(rate => {
            return {
              ...rate,
              isSelected: rate.code === selectedRate
            };
          })
        };
        this.setListViewInitialized(false);
      }
      this.loadCamperLock = false;
    });
    return res;
  }

  private createNewRates(rates: ApiCamperRate[], rate: ApiCamperRate): ApiCamperRate[] {
    let localRates = [...rates];
    if (rates.length > 0) {
      localRates = localRates.map(localRate => ({ ...localRate, isSelected: false }));
      localRates.splice(
        rates.findIndex(localRate => localRate.code === rate.code),
        1,
        { ...rate, isSelected: true }
      );
      return localRates;
    } else {
      return [];
    }
  }

  private static getSelectedOfferCodes(rates: ApiCamperRate[] | undefined): string[] | undefined {
    return rates
      ?.find(item => item.isSelected)
      ?.specialOfferGroups?.reduce((total: string[], current: ApiCamperSpecialOfferGroup) => {
        return [
          ...total,
          ...current.offers.filter(offer => offer.isSelected).map(offer => offer.code)
        ];
      }, []);
  }

  private getExtraSelectionsFromOffers(
    rate: ApiCamperRate
  ): { extrasInDeselectedOffers: string[]; extrasInSelectedOffers: ApiItemSelection[] } {
    let extrasInDeselectedOffers: string[] = [];
    let extrasInSelectedOffers: ApiItemSelection[] = [];
    rate?.specialOfferGroups?.forEach(group => {
      group.offers.forEach(offer => {
        offer?.details?.forEach(detail => {
          if (!!detail?.extras && detail.extras.length > 0) {
            if (!offer.isSelected) {
              extrasInDeselectedOffers = [
                ...extrasInDeselectedOffers,
                ...detail.extras.map(extra => extra.code)
              ];
            } else {
              extrasInSelectedOffers = [
                ...extrasInSelectedOffers,
                ...detail.extras.map(extra => ({
                  code: extra.code,
                  count: extra.selectedCount || 0
                }))
              ];
            }
          }
        });
      });
    });
    return { extrasInDeselectedOffers, extrasInSelectedOffers };
  }

  public async reloadCamperDetailsFromPreselection(preselectedRate: ApiCamperRate): Promise<void> {
    this.resetErrorMessages();
    if (!this.detail) return;

    const { extrasInDeselectedOffers, extrasInSelectedOffers } = this.getExtraSelectionsFromOffers(
      preselectedRate
    );

    const temp = {
      ...this.detail,
      rates: this.detail.rates?.map(rate => ({
        ...rate,
        isSelected: rate.code === preselectedRate.code
      }))
    };

    await this.loadCamperDetail(
      temp.id,
      undefined,
      CamperStore.getSelectedOfferCodes(temp.rates),
      [
        ...(temp.rates
          ?.find(rate => rate.isSelected)
          ?.extras?.map(extra => {
            return { code: extra.code, count: extra.selectedCount } as ApiItemSelection;
          })
          .filter(
            selection =>
              !extrasInSelectedOffers.find(extraInOffer => selection.code === extraInOffer.code)
          ) || []),
        ...extrasInSelectedOffers
      ].filter(itemSelection => !extrasInDeselectedOffers.includes(itemSelection.code)),
      preselectedRate.code
    );
  }

  public async reloadCamperDetailsFromReplacedRate(
    rate: ApiCamperRate,
    isSpecialChange?: boolean
  ): Promise<void> {
    this.resetErrorMessages();
    if (!this.detail) return;

    let extrasInDeselectedOffers: string[] = [];
    let extrasInSelectedOffers: ApiItemSelection[] = [];
    if (isSpecialChange) {
      const {
        extrasInDeselectedOffers: extrasInDeselectedOffersLocal,
        extrasInSelectedOffers: extrasInSelectedOffersLocal
      } = this.getExtraSelectionsFromOffers(rate);
      extrasInDeselectedOffers = [...extrasInDeselectedOffersLocal];
      extrasInSelectedOffers = [...extrasInSelectedOffersLocal];
    }

    const temp = {
      ...this.detail,
      rates: this.createNewRates(this.detail.rates || [], rate)
    };

    await this.loadCamperDetail(
      temp.id,
      undefined,
      CamperStore.getSelectedOfferCodes(temp.rates),
      [
        ...(temp.rates
          ?.find(item => item.isSelected)
          ?.extras?.map(extra => {
            return { code: extra.code, count: extra.selectedCount } as ApiItemSelection;
          })
          .filter(
            selection =>
              !extrasInSelectedOffers.find(extraInOffer => selection.code === extraInOffer.code)
          ) || []),
        ...extrasInSelectedOffers
      ].filter(
        itemSelection =>
          !!itemSelection.count && !extrasInDeselectedOffers.includes(itemSelection.code)
      ),
      temp.rates?.find(item => item.isSelected)?.code
    );
  }

  scrollToGlobalError(): void {
    scroller.scrollTo(this.scrollElementGlobalError, {
      duration: 750,
      smooth: true
    });
  }

  setError(value: string): void {
    if (value) {
      errorEvent.broadcast({ id: value });
    }
    this.error = value;
  }

  setErrorCamperNotFound(value: boolean): void {
    this.errorCamperNotFound = value;
  }

  resetFilters(): void {
    this._filter = this._defaultFilter;
  }
}

export default CamperStore;
